import { FormField, FormGrid } from '@dixa/lib-shared-habla';
import { isQueryError, isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect, useMemo } from 'react';
import { usePreviousDistinct } from 'react-use';

import { ApiErrorStatusCode } from 'api/api';
import {
  CustomFieldAttribute,
  CustomFieldAttributeValue,
  customFieldsApi,
  CustomFieldSelectAttribute,
  CustomFieldStringAttributeValue,
  CustomFieldStringListAttributeValue,
  CustomFieldTextAttribute,
} from 'apis/customFields';
import { actions as conversationsActions } from 'components/conversation/actions/conversation';
import {
  CustomField,
  useRequiredAndVisibleCustomFields,
} from 'utils/customFields';
import { useCustomFieldsVisibility } from 'utils/customFields/useCustomFieldsVisibility';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useCurrentUserInAcw } from 'utils/user';

import { CustomFieldText } from './components/fieldText/customFieldText';
import { CustomFieldSelect } from './components/select/customFieldSelect';
import { ConversationCustomFieldsSaveValueFn } from './conversationCustomFieldsForm.types';

type CustomFieldsValuesFromProps = {
  /** The custom fields. */
  readonly customFields: readonly CustomField[];
  /** The conversation ID. */
  readonly csid: number;
};

/** Renders a form containing custom fields of a conversation. */
export const ConversationCustomFieldsForm = ({
  csid,
  customFields,
}: CustomFieldsValuesFromProps): JSX.Element => {
  const queue = useAppSelector(
    (state) => state.conversation.metaConversation.conversation.queue,
  );
  const missingRequiredAndVisibleCustomFields =
    useRequiredAndVisibleCustomFields(customFields);

  const visibility = useCustomFieldsVisibility(customFields, {
    queue: [queue?.id ?? ''],
  });
  const dispatch = useAppDispatch();
  const isAcw = useCurrentUserInAcw({ csid });
  const isAssignedToMe = useAppSelector(
    (state) => state.conversation.metaConversation.isAssignedToMe,
  );

  const hasEmptyRequiredAndVisibleAttributes = useMemo(
    () => missingRequiredAndVisibleCustomFields.length > 0,
    [missingRequiredAndVisibleCustomFields],
  );

  const selectReadValue = useMemo(
    () => (state) =>
      customFieldsApi.endpoints.readValue.select({
        entityId: 'conversation',
        entityInstanceId: `${csid}`,
      })(state),
    [csid],
  );

  const { error: readValueError } = useAppSelector(selectReadValue);

  const shouldUpsertQueryData =
    isQueryError(readValueError) &&
    readValueError.status === ApiErrorStatusCode.NotFound;

  // Need to track `csid` changes in addition to `hasEmptyRequiredAttributes`
  // because navigating through conversations, some conversations may
  // have missing required attributes and some may not. This causes
  // `hasEmptyRequiredAttributes` change. So, we need to track the previous
  // value of `csid` and `hasEmptyRequiredAttributes` combined. Then we can
  // check if `hasEmptyRequiredAttributes` of the same conversation is changed.
  const current = useMemo(
    () => ({
      csid,
      hasEmptyRequiredAndVisibleAttributes,
    }),
    [csid, hasEmptyRequiredAndVisibleAttributes],
  );

  const previous = usePreviousDistinct(current);

  useEffect(() => {
    if (
      isAcw &&
      isAssignedToMe &&
      previous?.csid === current.csid &&
      previous.hasEmptyRequiredAndVisibleAttributes &&
      !current.hasEmptyRequiredAndVisibleAttributes
    ) {
      dispatch(conversationsActions.conversationUnblockAcw({ csid }));
    }
  }, [
    csid,
    current.csid,
    current.hasEmptyRequiredAndVisibleAttributes,
    dispatch,
    isAcw,
    isAssignedToMe,
    previous?.csid,
    previous?.hasEmptyRequiredAndVisibleAttributes,
  ]);

  const [updateValue] = customFieldsApi.useUpdateValueMutation();

  const save: ConversationCustomFieldsSaveValueFn<
    CustomFieldAttribute,
    CustomFieldAttributeValue
  > = useCallback(
    async (attribute, patch) => {
      const { id: attributeId, entity_id: entityId, type } = attribute;

      const response = await updateValue({
        entityId,
        entityInstanceId: `${csid}`,
        patch,
      });

      if (isResponseWithData(response)) {
        if (shouldUpsertQueryData) {
          // We do optimistic updates on save. However, if there is already no
          // value saved for the current conversation, the readValue api will
          // return 404 response and the optimistic update will not work. Hence,
          // we have to manually update the cache after saving the first value
          // in order for the next optimistic updates to work.
          dispatch(
            customFieldsApi.util.upsertQueryData(
              'readValue',
              { entityId, entityInstanceId: `${csid}` },
              response.data,
            ),
          );
        }

        ampli.customAttributeValueUpdated({
          entityId,
          entityInstanceId: `${csid}`,
          attributeId,
          type,
        });

        return response.data;
      }

      return Promise.reject(response.error);
    },
    [csid, dispatch, shouldUpsertQueryData, updateValue],
  );

  const renderField = (customField: CustomField) => {
    if (customField.attribute.type === 'text') {
      return (
        <CustomFieldText
          isAcw={isAcw}
          customField={
            customField as CustomField<
              CustomFieldTextAttribute,
              CustomFieldStringAttributeValue
            >
          }
          save={save}
        />
      );
    }

    if (customField.attribute.type === 'select') {
      return (
        <CustomFieldSelect
          isAcw={isAcw}
          customField={
            customField as CustomField<
              CustomFieldSelectAttribute,
              CustomFieldStringListAttributeValue
            >
          }
          save={save}
        />
      );
    }
  };

  return (
    <FormGrid>
      {customFields.map((customField) => {
        const { attribute } = customField;
        if (!visibility.data[attribute.id]) {
          return null;
        }

        return (
          <FormField key={attribute.id}>{renderField(customField)}</FormField>
        );
      })}
    </FormGrid>
  );
};
